import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { createProperty } from '../api/properties'; // uses your existing exported function
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  title: '',
  address1: '',
  town: '',
  county: '',
  postcode: '',
  type: '',
  status: '',
  description: '',
  purchasePrice: '',
  rentalCost: '',
  image: null,
};

const propertyTypes = [
  { value: '', label: 'Select type' },
  { value: 'house', label: 'House' },
  { value: 'flat', label: 'Flat' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'hmo', label: 'HMO' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed-use', label: 'Mixed Use' },
];

const propertyStatuses = [
  { value: '', label: 'Select status' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
];

function AddProperty({ property, onSave, onCreate }) {
  const [form, setForm] = useState(property || initialState);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { run: runSubmission, isPending } = useSubmissionLock();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files && files[0]) {
      setForm((prev) => ({ ...prev, image: files[0] }));
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const buildPropertyPayload = () => {
    const formData = new FormData();

    for (const [key, value] of Object.entries(form)) {
      if (value === null || value === undefined || value === '') continue;
      if (key === 'image') continue;

      if (key === 'purchasePrice' || key === 'rentalCost') {
        formData.append(key, Number(value));
      } else {
        formData.append(key, value);
      }
    }

    if (form.image instanceof File) {
      formData.append('image', form.image, form.image.name);
    }

    return formData;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending('property:create:standalone')) return;

    await runSubmission('property:create:standalone', async () => {
      setSaving(true);
      console.log('[AddProperty] handleSubmit start');

      try {
        const payloadToSend = buildPropertyPayload();

        let created = null;
        try {
          created = await createProperty(payloadToSend);
          console.log('[AddProperty] createProperty returned:', created);
          if (created && created.data) created = created.data;
        } catch (apiErr) {
          console.error('[AddProperty] createProperty API error', apiErr);
          alert(apiErr?.response?.data?.error || 'Failed to create property. Please try again.');
          return;
        }

        // 2) Call parent callbacks (await them if they return promises)
        if (typeof onSave === 'function') {
          try {
            const maybe = onSave(created);
            if (maybe && typeof maybe.then === 'function') {
              await maybe;
            }
            console.log('[AddProperty] onSave called');
          } catch (err) {
            console.warn('[AddProperty] onSave threw', err);
          }
        }

        if (typeof onCreate === 'function') {
          try {
            const maybe2 = onCreate(created);
            if (maybe2 && typeof maybe2.then === 'function') {
              await maybe2;
            }
            console.log('[AddProperty] onCreate called');
          } catch (err) {
            console.warn('[AddProperty] onCreate threw', err);
          }
        }

        // 3) notify listeners BEFORE navigation/closing so same-page listeners receive it
        try {
          const evt = new CustomEvent('properties:updated', { detail: { created } });
          window.dispatchEvent(evt);
          console.log('[AddProperty] dispatched properties:updated CustomEvent', created);
        } catch (err) {
          console.warn('[AddProperty] dispatch event failed', err);
        }

        // 4) write localStorage fallback (useful for after navigation or cross-tab)
        try {
          localStorage.setItem('properties:updated', JSON.stringify({ created, ts: Date.now() }));
          console.log('[AddProperty] wrote localStorage properties:updated');
        } catch (err) {
          console.warn('[AddProperty] localStorage write failed', err);
        }

        // 5) reset form (UX)
        setForm(initialState);
        setImagePreview(null);

        // 6) navigate only if parent didn't handle it
        const parentHandled = typeof onSave === 'function' || typeof onCreate === 'function';
        if (!parentHandled) {
          console.log('[AddProperty] no parent handler — navigating to /properties');
          navigate('/properties');
        } else {
          console.log('[AddProperty] parent handler present — not navigating');
        }
      } catch (err) {
        console.error('[AddProperty] unexpected error during submit', err);
        alert('Error creating property');
      } finally {
        setSaving(false);
        console.log('[AddProperty] handleSubmit finished');
      }
    });
  };


  return (
    <div className="max-w-2xl mx-auto mt-10 bg-white/10 rounded-xl border border-white/10 p-8 backdrop-blur-md text-white">
      <button
        className="flex items-center gap-2 mb-6 text-white hover:text-[#AF9B8A] transition"
        onClick={() => navigate(-1)}
        type="button"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>
      <h2 className="text-2xl font-bold mb-6">{property ? 'Edit Property' : 'Add Property'}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block mb-1 font-semibold">Title</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            placeholder="Property title"
          />
        </div>
        <div>
          <label className="block mb-1 font-semibold">Address</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="address1"
              value={form.address1}
              onChange={handleChange}
              required
              className="px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="Number / Street Name"
            />
            <input
              type="text"
              name="town"
              value={form.town}
              onChange={handleChange}
              required
              className="px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="Town"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <input
              type="text"
              name="county"
              value={form.county}
              onChange={handleChange}
              required
              className="px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="County"
            />
            <input
              type="text"
              name="postcode"
              value={form.postcode}
              onChange={handleChange}
              required
              className="px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="Postcode"
            />
          </div>
        </div>
        <div className="flex gap-4">
  <div className="flex-1 relative">
    <label className="block mb-1 font-semibold">Type</label>
    <select
      name="type"
      value={form.type}
      onChange={handleChange}
      required
      className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
      style={{ cursor: 'pointer' }}
    >
      {propertyTypes.map((t) => (
        <option key={t.value} value={t.value}>{t.label}</option>
      ))}
    </select>
            <ChevronDown className="absolute right-4 top-10 pointer-events-none w-4 h-4 text-white/70" />
  </div>
  <div className="flex-1 relative">
    <label className="block mb-1 font-semibold">Status</label>
    <select
      name="status"
      value={form.status}
      onChange={handleChange}
      required
      className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
      style={{ cursor: 'pointer' }}
    >
      {propertyStatuses.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>
                <ChevronDown className="absolute right-4 top-10 pointer-events-none w-4 h-4 text-white/70" />
  </div>
</div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Purchase Price (£)</label>
            <input
              type="number"
              name="purchasePrice"
              value={form.purchasePrice}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="e.g. 250000"
            />
          </div>
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Rental Cost (£/month)</label>
            <input
              type="number"
              name="rentalCost"
              value={form.rentalCost}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="e.g. 1600"
            />
          </div>
        </div>
        <div>
          <label className="block mb-1 font-semibold">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            placeholder="Property description"
          />
        </div>
        <div>
          <label className="block mb-1 font-semibold">Upload Image</label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#AF9B8A]/20 file:text-white hover:file:bg-[#AF9B8A]/40"
            />
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
            )}
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-60 disabled:cursor-not-allowed"
          aria-busy={saving}
        >
          {saving ? (property ? 'Saving...' : 'Adding property...') : (property ? 'Save Changes' : 'Add Property')}
        </button>
      </form>
    </div>
  );
}

export default AddProperty;
